import { generalColors, specificColors } from './colors';
// eslint-disable-next-line no-unused-vars
import { LegacyTheme } from '../types';

const cfk = (copy, fill, keyline) => ({ copy, fill, keyline });
const transparent = 'transparent';

// TODO: FPLAT-288 remove box shadows
export const hexToRgb = (hex: string): string | null => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16,
      )}`
    : null;
};

const box_shadow_100 = (colour: string): string =>
  `0 4px 8px 0 rgba(${hexToRgb(colour)}, .32)`;
const box_shadow_200 = (colour: string): string =>
  `0 8px 12px 0 rgba(${hexToRgb(colour)}, .25)`;
const box_shadow_300 = (colour: string): string =>
  `0 16px 24px 0 rgba(${hexToRgb(colour)}, .20)`;
const box_shadow_400 = (colour: string): string =>
  `0 24px 24px 0 rgba(${hexToRgb(colour)}, .25)`;
const box_shadow_500 = (colour: string): string =>
  `0 32px 20px -16px rgba(${hexToRgb(colour)}, .5)`;

const {
  darkGrey20,
  darkGrey60,
  darkGrey80,
  lightGrey20,
  darkGrey10,
  primary10,
  primary20,
  primary,
  accent,
  accent10,
  accent40,
  accent60,
  accent80,
  complementaryOrange,
  complementaryRed,
  complementaryRed10,
  complementaryRed40,
  primary40,
  lightGrey10,
  complementaryGreen,
  complementaryGreen10,
  complementaryGreen40,
  secondary40,
  secondary,
  secondary10,
  primary30,
  darkGrey,
} = generalColors;

const {
  twitter,
  facebook,
  google,
  linkedin,
  youtube,
  instagram,
  flickr,
  tumblr,
  pinterest,
} = specificColors.socialMedia;

const family_primary = '"Source Sans Pro", "Helvetica Neue", Helvetica, Arial';
const family_secondary = 'Arial, Helvetica, sans-serif';
const family_serif = 'Georgia, "Times New Roman", Times, serif';
const family_monospaced = '"Courier New", Courier, monospace';

const size_xlarge = '48px';
const size_h1 = '28px';
const size_h2 = '24px';
const size_h3 = '20px';
const size_h4 = '16px';
const size_body = '16px';
const size_small = '14px';
const size_tiny = '12px';

export const legacyToBrand2020: LegacyTheme = {
  typography: {
    fontFamily: {
      primary: family_primary,
      secondary: family_secondary,
      serif: family_serif,
      monospaced: family_monospaced,
    },

    weight: {
      normal: '400',
      semi: '600',
      bold: '700',
    },

    size: {
      h1: size_h1,
      h2: size_h2,
      h3: size_h3,
      h4: size_h4,
      body: size_body,
      copy: size_body,
      paragraph: size_body,
      small: size_small,
      tiny: size_tiny,
      button: size_body,
      xlarge: size_xlarge,
    },
  },
  colors: {
    // TODO: FPLAT-288 in the old theme social media colors had white for copy, replaced with lightGrey10, check if ok.
    twitter: cfk(lightGrey10, twitter, twitter),
    facebook: cfk(lightGrey10, facebook, facebook),
    google: cfk(lightGrey10, google, google),
    linkedin: cfk(lightGrey10, linkedin, linkedin),
    youtube: cfk(lightGrey10, youtube, youtube),
    instagram: cfk(lightGrey10, instagram, instagram),
    // TODO: FPLAT-288 deprecate in new theme
    flickr: cfk(lightGrey10, flickr, flickr),
    // TODO: FPLAT-288 deprecate in new theme
    tumblr: cfk(lightGrey10, tumblr, tumblr),
    pinterest: cfk(lightGrey10, pinterest, pinterest),

    body: cfk(darkGrey80, lightGrey20, darkGrey20),
    active: cfk(darkGrey80, darkGrey10, darkGrey10),
    altActive: cfk(darkGrey80, primary10, primary20),
    mute: cfk(darkGrey60, transparent, transparent),
    accent: cfk(darkGrey20, primary, primary),
    alt: cfk(darkGrey80, darkGrey10, darkGrey20),
    contrast: cfk(lightGrey10, darkGrey80, darkGrey20),
    // TODO: FPLAT-288 deprecate in new theme
    gradient: {
      primary: cfk(lightGrey10, primary, primary),
    },
    // TODO: FPLAT-288 deprecate in new theme
    shadow: {
      box_shadow_100: box_shadow_100(darkGrey80),
      box_shadow_200: box_shadow_200(darkGrey80),
      box_shadow_300: box_shadow_300(darkGrey80),
      box_shadow_400: box_shadow_400(darkGrey80),
      box_shadow_500: box_shadow_500(darkGrey80),
    },
    info: cfk(secondary, secondary10, secondary40),
    timestamp: darkGrey60,
    anchor: secondary,
    overlay: {
      ...cfk(darkGrey80, lightGrey10, darkGrey20),
      alt: cfk(darkGrey80, darkGrey10, darkGrey20), // hover
      altActive: cfk(lightGrey10, darkGrey60, darkGrey60), // focus
      active: cfk(darkGrey80, darkGrey10, darkGrey10),
      mute: cfk(darkGrey60, transparent, transparent),
      accent: cfk(lightGrey10, darkGrey80, lightGrey10),
      altAccent: cfk(darkGrey20, primary, primary),
      contrast: cfk(lightGrey10, darkGrey80, darkGrey20),
      // Added to temporarily solve theme keys errors
      // TODO: FPLAT-288 remove this after migration is done
      copy: '',
    },

    input: {
      active: cfk(darkGrey, lightGrey10, complementaryOrange),
      error: cfk(darkGrey, lightGrey10, complementaryRed),
      checkbox: cfk(darkGrey, lightGrey10, darkGrey60),
      text: cfk(darkGrey, lightGrey10, darkGrey60),
      // Added to temporarily solve theme keys errors
      // TODO: FPLAT-288 remove this after migration is done
      copy: '',
    },
    error: cfk(complementaryRed, complementaryRed10, complementaryRed40),
    warning: cfk(accent, accent10, accent40),
    success: cfk(
      complementaryGreen,
      complementaryGreen10,
      complementaryGreen40,
    ),

    button: {
      primary: {
        ...cfk(primary, primary20, primary20),
        hover: cfk(primary, primary30, primary30),
        focus: cfk(primary, primary30, complementaryOrange),
        active: cfk(primary, primary40, primary40),
        disabled: cfk(primary40, primary10, primary10),
      },
      secondary: {
        ...cfk(primary, lightGrey10, primary20),
        hover: cfk(primary, primary30, primary30),
        focus: cfk(primary, primary30, complementaryOrange),
        active: cfk(primary, primary40, primary40),
        disabled: cfk(primary40, primary10, complementaryOrange),
      },
      cta: {
        ...cfk(primary, accent, accent),
        hover: cfk(primary, accent60, accent60),
        focus: cfk(primary, accent60, complementaryOrange),
        active: cfk(primary, accent80, accent80),
        disabled: cfk(primary40, accent10, complementaryOrange),
      },
      icon: {
        ...cfk(primary, primary20, primary20),
        hover: cfk(primary, primary30, primary30),
        focus: cfk(primary, primary30, complementaryOrange),
        active: cfk(primary, primary40, primary40),
        disabled: cfk(primary40, primary10, primary10),
      },
      standard: {
        ...cfk(primary, primary20, primary20),
        hover: cfk(primary, primary30, primary30),
        focus: cfk(primary, primary30, complementaryOrange),
        active: cfk(primary, primary40, primary40),
        disabled: cfk(primary40, primary10, primary10),
      },
      // Added to temporarily solve theme keys errors
      // TODO: FPLAT-288 remove this after migration is done
      copy: '',
    },

    // Added to temporarily solve theme keys errors
    // TODO: FPLAT-288 remove this after migration is done
    contrastHeader: {
      keyline: '',
    },
  },
};
